import {
	type CustomField,
	getFlagColor,
	getTicketFields,
	TicketStatusFilter,
	type TicketType,
	type TicketTypePriority,
} from '@cheqroom/api';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';

import type { FilterFieldEntry, FilterFieldOption } from './types';
import { getDynamicFilterEntries } from './utils/get-dynamic-filter-entries';

const EMPTY_HIDE_FILTERS: string[] = [];

type TicketFilterLabel = {
	id: string | null;
	name: string;
	color: string;
	ticketTypeId?: string | null;
};

type TicketFiltersTicketType = Pick<TicketType, 'id' | 'system_fields_visibility'> & {
	settings?: Pick<TicketType['settings'], 'priorities'>;
};

export const toFilterLabelOptions = (labels: TicketFilterLabel[]): FilterFieldOption[] =>
	labels.map((label) => ({
		label: label.name,
		value: label.id ?? '',
		color: label.color,
		icon: 'RoundedSquare',
	}));

export const toFilterPriorityOptions = (priorities: TicketTypePriority[]): FilterFieldOption[] =>
	priorities.map((priority) => ({
		label: priority.name,
		value: priority.name,
		color: priority.color,
		icon: priority.icon,
	}));

const getTicketStatusOptions = (t: TFunction<'ticket-filters'>) =>
	Object.values(TicketStatusFilter).map((status) => ({
		label: t(`ticket-filters:status.options.${status}`),
		value: status,
	}));

const labelsForTicketType = (labels: TicketFilterLabel[], ticketTypeId?: string) => {
	if (!ticketTypeId) return labels;
	return labels.filter((label) => label.ticketTypeId === ticketTypeId || label.id == null || label.id === '');
};

const withUnlabeledOption = (t: TFunction<'common'>, labels: TicketFilterLabel[]): TicketFilterLabel[] =>
	labels.some((label) => label.id == null)
		? labels
		: [{ id: null, name: t('common:filter.labels.unlabeled'), color: 'ORANGE' }, ...labels];

const withResolvedColors = (labels: TicketFilterLabel[]): TicketFilterLabel[] =>
	labels.map((label) => ({ ...label, color: label.color.startsWith('#') ? label.color : getFlagColor(label.color) }));

interface UseTicketFiltersProps {
	t: TFunction<'ticket-filters' | 'common'>;
	ticketType?: TicketFiltersTicketType | null;
	customFields?: CustomField[];
	hideFilters?: string[];
	labels?: TicketFilterLabel[];
}

export const useTicketFilters = ({
	t,
	ticketType,
	customFields,
	hideFilters = EMPTY_HIDE_FILTERS,
	labels,
}: UseTicketFiltersProps) => {
	const filters = useMemo<FilterFieldEntry[]>(() => {
		const priorities = ticketType?.settings?.priorities ?? [];
		const resolvedHideFilters = priorities.length > 0 ? hideFilters : [...hideFilters, 'priorities'];

		const allStaticFilters: FilterFieldEntry[] = [
			{
				id: 'ticketStatuses',
				name: t('ticket-filters:status.trigger'),
				type: 'select',
				options: getTicketStatusOptions(t),
			},
			{ id: 'locations', name: t('filter.location.trigger') },
			{
				id: 'labels',
				name: t('filter.labels.trigger'),
				type: 'select',
				options: toFilterLabelOptions(
					labelsForTicketType(withResolvedColors(withUnlabeledOption(t, labels ?? [])), ticketType?.id)
				),
			},
			{ id: 'reporters', name: t('ticket-filters:reporter.trigger'), type: 'customer' },
			{ id: 'assignees', name: t('ticket-filters:assignee.trigger'), type: 'customer' },
			{
				id: 'priorities',
				name: t('ticket-filters:priority.trigger'),
				type: 'select',
				options: toFilterPriorityOptions(priorities),
			},
		];
		const staticFilters = allStaticFilters.filter((entry) => !resolvedHideFilters.includes(entry.id));

		const ticketFields = getTicketFields({
			systemFieldsVisibility: ticketType?.system_fields_visibility,
			customFields,
		});
		const dynamicFilters = getDynamicFilterEntries(
			ticketFields.filter((field) => !resolvedHideFilters.includes(field.id))
		);

		return [...staticFilters, ...dynamicFilters];
	}, [t, ticketType, customFields, hideFilters, labels]);

	return {
		filters,
	};
};
